/**
 * Q&A Library Page
 *
 * Epic 16: Manage saved question/answer pairs for screening questions.
 * Per design.md: Premium UI with semantic tokens, lucide icons, no emojis.
 */

import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Doc, Id } from 'convex/_generated/dataModel';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Card, CardContent,
    Button, Badge, Input, Skeleton,
    Plus, Search, MessageSquareText, Trash2, Edit, Hash, User,
} from '@uprosal/ui';
import { QADialog } from './QADialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';

// Type for Q&A item without embedding
type QAItem = Omit<Doc<'questionAnswers'>, 'embedding'>;
type Profile = Doc<'connectedProfiles'>;

export function QALibraryPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [tagFilter, setTagFilter] = useState<string | null>(null);
    const [profileFilter, setProfileFilter] = useState<Id<'connectedProfiles'> | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingQA, setEditingQA] = useState<QAItem | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<QAItem | null>(null);

    // Fetch Q&A items from Convex
    const qaItems = useQuery(api.queries.questionAnswers.list, {}) as QAItem[] | undefined;
    const profiles = useQuery(api.queries.profiles.listConnected, {}) as Profile[] | undefined;
    const archiveQA = useMutation(api.mutations.questionAnswers.archive);

    // Extract all unique tags
    const allTags = qaItems
        ? Array.from(new Set(qaItems.flatMap((item: QAItem) => item.tags)))
        : [];

    // Filter Q&A items
    const filteredItems = qaItems?.filter((item: QAItem) => {
        // Profile filter: show items matching the profile OR global items (no profileId)
        if (profileFilter) {
            const isProfileMatch = item.profileId === profileFilter;
            const isGlobal = !item.profileId;
            if (!isProfileMatch && !isGlobal) {
                return false;
            }
        }

        // Tag filter
        if (tagFilter && !item.tags.includes(tagFilter)) {
            return false;
        }

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                item.questionPattern.toLowerCase().includes(query) ||
                item.answer.toLowerCase().includes(query) ||
                item.tags.some(t => t.includes(query))
            );
        }

        return true;
    }) ?? [];

    const handleNewQA = () => {
        setEditingQA(null);
        setDialogOpen(true);
    };

    const handleEditQA = (qa: QAItem) => {
        setEditingQA(qa);
        setDialogOpen(true);
    };

    const handleDeleteQA = (qa: QAItem) => {
        setDeleteConfirm(qa);
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        try {
            await archiveQA({ qaId: deleteConfirm._id });
        } catch (error) {
            console.error('Failed to archive Q&A item:', error);
        }
        setDeleteConfirm(null);
    };

    const isLoading = qaItems === undefined;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Q&A Library</h1>
                    <p className="text-muted-foreground mt-1">
                        Save answers to common screening questions for quick access
                    </p>
                </div>
                <Button className="flex items-center gap-2" onClick={handleNewQA}>
                    <Plus className="w-4 h-4" />
                    Add Q&A
                </Button>
            </div>

            {/* Filters Row: Profile Dropdown + Tag Pills */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                {/* Profile Filter Dropdown */}
                {profiles && profiles.length > 0 && (
                    <div className="flex items-center gap-2 shrink-0">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <select
                            value={profileFilter ?? ''}
                            onChange={(e) => setProfileFilter(e.target.value ? e.target.value as Id<'connectedProfiles'> : null)}
                            className="w-[180px] px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                            <option value="">All Profiles</option>
                            {profiles.map((profile) => (
                                <option key={profile._id} value={profile._id}>
                                    {profile.profileLabel}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Tag Filter Pills */}
                {allTags.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={() => setTagFilter(null)}
                            className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                                tagFilter === null
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                            }`}
                        >
                            All
                        </button>
                        {allTags.slice(0, 10).map((tag) => (
                            <button
                                key={tag}
                                onClick={() => setTagFilter(tag === tagFilter ? null : tag)}
                                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                                    tagFilter === tag
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                                }`}
                            >
                                <Hash className="w-3 h-3 inline mr-1" />
                                {tag}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Search */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        type="text"
                        placeholder="Search questions, answers, or tags..."
                        className="w-full pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                {qaItems && (
                    <Badge variant="secondary" className="shrink-0">
                        {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
                    </Badge>
                )}
            </div>

            {/* Loading state */}
            {isLoading && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                        <Card key={i}>
                            <CardContent className="pt-6">
                                <div className="flex items-start justify-between mb-3">
                                    <Skeleton className="w-10 h-10 rounded-lg" />
                                    <Skeleton className="w-6 h-6" />
                                </div>
                                <Skeleton className="h-5 w-full mb-2" />
                                <Skeleton className="h-4 w-full mb-1" />
                                <Skeleton className="h-4 w-2/3 mb-3" />
                                <div className="flex gap-2">
                                    <Skeleton className="h-5 w-16" />
                                    <Skeleton className="h-5 w-16" />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Empty state */}
            {!isLoading && filteredItems.length === 0 && (
                <Card className="py-12">
                    <CardContent className="text-center">
                        <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                            <MessageSquareText className="w-8 h-8 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                            {searchQuery || tagFilter || profileFilter
                                ? 'No matching Q&A items'
                                : 'No saved Q&A yet'}
                        </h3>
                        <p className="text-muted-foreground mb-4">
                            {searchQuery || tagFilter || profileFilter
                                ? 'Try a different search term, tag, or profile filter.'
                                : 'Save your best answers to common screening questions.'}
                        </p>
                        {!searchQuery && !tagFilter && !profileFilter && (
                            <Button onClick={handleNewQA}>
                                <Plus className="w-4 h-4 mr-2" />
                                Add Your First Q&A
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Q&A Items Grid */}
            <AnimatePresence mode="wait">
                {!isLoading && filteredItems.length > 0 && (
                    <motion.div
                        key="qa-grid"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    >
                        {filteredItems.map((item: QAItem, i: number) => (
                            <motion.div
                                key={item._id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: i * 0.05 }}
                                className="h-full"
                            >
                                <Card className="group cursor-pointer hover:shadow-lg transition-shadow h-full flex flex-col">
                                    <CardContent className="pt-6 flex flex-col flex-1">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="p-2 rounded-lg bg-primary/10">
                                                <MessageSquareText className="w-5 h-5 text-primary" />
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {item.usageCount > 0 && (
                                                    <Badge variant="outline" className="text-xs">
                                                        Used {item.usageCount}x
                                                    </Badge>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                                                    title="Edit"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditQA(item);
                                                    }}
                                                >
                                                    <Edit className="w-4 h-4 text-muted-foreground" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                                                    title="Delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteQA(item);
                                                    }}
                                                >
                                                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Question */}
                                        <h3
                                            className="font-semibold text-foreground mb-2 line-clamp-2"
                                            onClick={() => handleEditQA(item)}
                                            title={item.questionPattern}
                                        >
                                            {item.questionPattern}
                                        </h3>

                                        {/* Answer preview */}
                                        <p className="text-sm text-muted-foreground line-clamp-3 mb-3 flex-1">
                                            {item.answer}
                                        </p>

                                        {/* Tags */}
                                        <div className="flex flex-wrap gap-1 mb-3 min-h-[1.5rem]">
                                            {item.tags.slice(0, 4).map((tag) => (
                                                <Badge
                                                    key={tag}
                                                    variant="secondary"
                                                    className="text-xs cursor-pointer hover:bg-secondary/80"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setTagFilter(tag);
                                                    }}
                                                >
                                                    {tag}
                                                </Badge>
                                            ))}
                                            {item.tags.length > 4 && (
                                                <Badge variant="secondary" className="text-xs">
                                                    +{item.tags.length - 4}
                                                </Badge>
                                            )}
                                        </div>

                                        {/* Footer */}
                                        <div className="flex items-center justify-between text-xs mt-auto">
                                            <span className="text-muted-foreground">
                                                {new Date(item.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Create/Edit Dialog */}
            <QADialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                qa={editingQA}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={!!deleteConfirm}
                onOpenChange={(open) => !open && setDeleteConfirm(null)}
                title="Delete Q&A Item"
                description={
                    <>
                        Are you sure you want to delete this Q&A?
                        <br /><br />
                        <span className="text-sm text-muted-foreground">
                            "{deleteConfirm?.questionPattern.slice(0, 50)}..."
                        </span>
                    </>
                }
                confirmLabel="Delete"
                cancelLabel="Cancel"
                variant="destructive"
                onConfirm={confirmDelete}
            />
        </div>
    );
}
